import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

const DISMISSED_KEY = 'pwa-install-dismissed';

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isInStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState(null); // 'android' | 'ios'

  useEffect(() => {
    if (isInStandalone()) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    if (isIos()) {
      // Delay iOS banner slightly so it doesn't block login
      const t = setTimeout(() => {
        setPlatform('ios');
        setShow(true);
      }, 4000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform('android');
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShow(false);
    if (outcome === 'accepted') {
      localStorage.setItem(DISMISSED_KEY, '1');
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  if (!show) return null;

  return (
    <div className="pwa-install-banner">
      <div className="bg-[#033923] text-white rounded-2xl shadow-2xl p-4 flex items-start gap-3 border border-white/10">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <img src="/omlogo.png" alt="OM-HRMS" className="w-7 h-7 object-contain" />
        </div>

        <div className="flex-1 min-w-0">
          {platform === 'android' ? (
            <>
              <p className="font-semibold text-sm">Install OM-HRMS</p>
              <p className="text-xs text-white/75 mt-0.5">
                Add to home screen for faster access &amp; offline use
              </p>
              <button
                onClick={handleInstall}
                className="mt-2 inline-flex items-center gap-1.5 bg-white text-[#033923] text-xs font-semibold px-3 py-1.5 rounded-lg"
              >
                <Download className="w-3.5 h-3.5" />
                Install App
              </button>
            </>
          ) : (
            <>
              <p className="font-semibold text-sm">Add to Home Screen</p>
              <p className="text-xs text-white/75 mt-0.5">
                Tap <Share className="w-3 h-3 inline mx-0.5 align-middle" /> then
                <strong className="text-white"> "Add to Home Screen"</strong> for the best experience
              </p>
            </>
          )}
        </div>

        <button
          onClick={handleDismiss}
          className="p-1 text-white/60 hover:text-white flex-shrink-0 -mt-0.5"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
